import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import PasswordStrength, { isStrongPassword } from "../components/PasswordStrength";

export default function AdminChangePassword({ forced = false }) {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const strong = isStrongPassword(next);
  const submit = async (e) => {
    e.preventDefault();
    if (!strong) { toast.error("Please meet all password strength rules"); return; }
    if (next !== confirm) { toast.error("New passwords don't match"); return; }
    setSaving(true);
    try {
      await api.post("/users/me/password", { current_password: current, new_password: next });
      toast.success("Password updated");
      if (refresh) await refresh();
      navigate("/admin");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="change-password-page" className="min-h-[80vh] flex items-center justify-center px-6 py-20 bg-[#FAFAFA]">
      <form onSubmit={submit} className="w-full max-w-md bg-white border border-black/5 p-10">
        <div className="flex items-center gap-3 mb-2">
          <Lock size={18} className="text-[var(--brand-primary)]" />
          <span className="text-sm uppercase tracking-[0.2em] text-[#525252]">{forced ? "Set a new password" : "Change Password"}</span>
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight">{forced ? "Welcome — pick a new password" : "Update your password"}</h1>
        {forced && <p className="mt-2 text-[#525252] text-sm">For security, you must change the temporary password before continuing.</p>}

        <div className="mt-8 space-y-5">
          <div>
            <label className="block text-sm font-medium mb-2">{forced ? "Temporary password" : "Current password"}</label>
            <input data-testid="cp-current" required type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
                   className="w-full bg-white border border-black/10 rounded-sm px-4 py-3 focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)] outline-none"/>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">New password</label>
            <input data-testid="cp-new" required type="password" value={next} onChange={(e) => setNext(e.target.value)}
                   className="w-full bg-white border border-black/10 rounded-sm px-4 py-3 focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)] outline-none"/>
            <PasswordStrength password={next} testId="cp-strength" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Confirm new password</label>
            <input data-testid="cp-confirm" required type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                   className="w-full bg-white border border-black/10 rounded-sm px-4 py-3 focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)] outline-none"/>
            {confirm.length > 0 && confirm !== next && (
              <p data-testid="cp-mismatch" className="mt-2 text-xs text-rose-700">Passwords don't match</p>
            )}
          </div>
        </div>

        <div className="mt-8 flex gap-3">
          {!forced && (
            <button
              type="button"
              data-testid="cp-cancel"
              onClick={() => navigate("/admin")}
              className="flex-1 bg-white text-[#0A0A0A] border border-black/10 hover:bg-black/5 px-6 py-3 rounded-sm font-medium"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={saving || !strong || next !== confirm}
            data-testid="cp-submit"
            className="flex-1 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-6 py-3 rounded-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Update Password"}
          </button>
        </div>
      </form>
    </div>
  );
}
